/**
 * DropHubFeaturedCards — the right column's default occupants on the HUB
 * stage: the featured roster member, then every video the Drop authored.
 *
 * Extracted out of `DropHubSurface` to keep that file under its own
 * 400-line ceiling — this `SlotRole.FEATURE` block was its single largest
 * inline slot renderer. Same split `DropPageHero` already is for the PAGE
 * tree's hero switch.
 *
 * Still a JOIN, not a transformation of its own: every prop is plain data or
 * a callback the caller owns (`onWatchFeatured`/`onWatchTrailer`), so this
 * file knows a Drop and the roster shape but never touches `stageOccupant`
 * state directly — that stays `DropHubSurface`'s.
 */

import React from "react";
import {StreamInlinePlayer} from "@/widgets/StreamInlinePlayer";
import {highlightForMember, memberKey, type RosterMember} from "@/widgets/rosterMember";
import {SurfaceFeaturedColumn} from "@/surfaces/spine/SurfaceFeaturedColumn";
import {isOffTitle, suppressesOffTitle} from "@/surfaces/spine/dropSpineData";
import type {PovCopy} from "@/surfaces/spine/povCopy";
import {youTubeId, youTubeThumb} from "@/surfaces/spine/youTubeThumb";
import type {SurfaceStageOccupant} from "@/surfaces/spine/SurfaceStage";

export interface DropHubFeaturedCardsProps {
    featured?: RosterMember;
    onTitle: readonly RosterMember[];
    filterGameNames: readonly string[];
    /** The roster's LiveFilterMode. STRICT means the off-title kicker is not said. */
    filterMode?: string;
    highlights: Parameters<typeof highlightForMember>[1];
    copy: PovCopy;
    drop: any;
    trailerThumb?: string;
    onWatchFeatured: (m: RosterMember) => void;
    onWatchTrailer: (occupant: SurfaceStageOccupant) => void;
}

interface AuthoredVideo {
    url?: string;
    title?: string;
}

/**
 * The Drop's videos, as playable cards.
 *
 * `trailerUrl` is a pointer INTO this list rather than a separate video, so the
 * matching entry leads and is not repeated. When nothing is authored, the
 * trailer still stands alone — a Drop that only ever set `trailerUrl` keeps the
 * card it always had.
 */
function videoCards(
    drop: any,
    trailerThumb: string | undefined,
    onWatchTrailer: DropHubFeaturedCardsProps["onWatchTrailer"],
) {
    const authored = (drop?.videos as AuthoredVideo[] | undefined) ?? [];
    /*
     * NO TRAILER SET IS NOT NO VIDEOS. `trailerUrl` is a separate field, and an
     * operator who fills the strip but leaves it blank — or whose save clears
     * it — used to lose the whole row. The admin calls this list "under the
     * featured video", so the first entry IS the featured one when nothing else
     * claims the role.
     */
    const leadUrl = drop?.trailerUrl || authored[0]?.url;
    const list: AuthoredVideo[] = authored.length
        ? [
            ...authored.filter(v => v.url && v.url === leadUrl),
            ...authored.filter(v => !v.url || v.url !== leadUrl),
        ]
        : drop?.trailerUrl
            ? [{url: drop.trailerUrl, title: drop.tagline ?? drop.name}]
            : [];

    return list.flatMap((video, i) => {
        const isTrailer = Boolean(video.url && video.url === leadUrl);
        /* The trailer's still is already resolved upstream; the rest derive theirs. */
        const imageUrl = isTrailer ? trailerThumb ?? youTubeThumb(video.url) : youTubeThumb(video.url);
        const videoId = youTubeId(video.url);
        if (!imageUrl || !videoId) return [];

        const title = video.title ?? (isTrailer ? drop?.tagline ?? drop?.name : undefined) ?? "";
        return [{
            id: `video-${videoId}-${i}`,
            imageUrl,
            title,
            playable: true,
            onClick: () => onWatchTrailer({
                player: <StreamInlinePlayer
                    source={{
                        platform: "YOUTUBE",
                        youtubeVideoId: videoId,
                        isLive: false,
                        title,
                        autoplay: true,
                        muted: true,
                        viewerInitiated: true,
                    }}
                />,
                imageUrl,
                title,
                subtitle: drop?.name,
            }),
        }];
    });
}

export const DropHubFeaturedCards: React.FC<DropHubFeaturedCardsProps> = ({
    featured, onTitle, filterGameNames, filterMode, highlights, copy, drop, trailerThumb, onWatchFeatured, onWatchTrailer,
}) =>
    <SurfaceFeaturedColumn
        cards={[
            ...featured
                ? [{
                    id: "featured-member",
                    imageUrl: featured.thumbnailUrl ?? featured.profileImageUrl,
                    live: featured.isLive,
                    liveMeta: featured.isLive && featured.viewerCount
                        ? `${featured.viewerCount.toLocaleString()} watching`
                        : undefined,
                    /*
                     * `featured` is drawn from `onTitle` first, so this only
                     * says "off title" when the WHOLE roster is off-title and
                     * the fallback to `members[0]` fired. Under STRICT even
                     * that is a claim the roster's operator has switched off,
                     * so the plain kicker stands in — same rule as the rail's
                     * badge, read from the same helper rather than restated.
                     */
                    kicker: isOffTitle(featured, filterGameNames) && !suppressesOffTitle(filterMode)
                        ? copy.rosterKickerOffTitle
                        : copy.rosterKicker,
                    kickerMeta: featured.platform
                        ? featured.platform.charAt(0) + featured.platform.slice(1)
                            .toLowerCase()
                        : undefined,
                    title: featured.label ?? "",
                    /*
                     * Not live: say what IS there. A clip's own title tells
                     * the viewer something is actually watchable; leaving this
                     * blank when a clip exists undersells the one card on the
                     * surface that has real content behind it.
                     */
                    description: featured.isLive
                        ? featured.streamTitle
                        : highlightForMember(featured, highlights)?.title,
                    faces: onTitle
                        /* memberKey, not dropSlug — see the same note in rosterMembers(). */
                        .filter(m => memberKey(m) !== memberKey(featured))
                        .slice(0, 3)
                        .map(m => ({
                            id: memberKey(m),
                            initials: (m.label ?? "").slice(0, 2)
                                .toUpperCase(),
                        })),
                    facesLabel: onTitle.length > 1
                        ? `+${onTitle.length - 1} ${copy.rosterFacesNoun}`
                        : undefined,
                    onClick: () => onWatchFeatured(featured),
                }]
                : [],
            /*
             * EVERY authored video, not just the trailer.
             *
             * The Drop's video strip used to reach the surface for one purpose:
             * lending its title to the single trailer card. Everything else an
             * operator added rendered nowhere, so the row sat half empty beside
             * a strip that looked saved and did nothing.
             *
             * The trailer leads — it is the one the operator named as such —
             * and the rest follow in authored order. A video whose still cannot
             * be derived is dropped rather than shown as a dark box: the
             * artwork IS the card.
             */
            ...videoCards(drop, trailerThumb, onWatchTrailer),
        ]}
    />;

export default DropHubFeaturedCards;
